import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { LangService } from '../../../shared/services/lang/lang.service';
import { menuItemsToSidebarItems, SidebarItem } from '../sidebar.types';
import { MenuParamsService } from './menu-params.service';
import { PageConfig, PAGES_CONFIG } from 'src/app/shared/page/page.config';
import { Store } from '@ngrx/store';
import {
  PageActions,
  pageFeature,
} from 'src/app/shared/page/store/page.feature';
import { Lang } from 'src/app/shared/services/lang/lang.types';
import { MenuConfig, MenuItemName } from 'src/app/shared/page/menu.config';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private langService = inject(LangService);
  private store = inject(Store);
  private menuParamsService = inject(MenuParamsService);

  private menuParams$ = this.menuParamsService.menuParams$;
  private menuItems = MenuConfig;
  private lang$ = this.langService.onLang().pipe(map(({ lang }) => lang));
  sidebarItems$: Observable<SidebarItem[]> = combineLatest([
    this.menuParams$,
    this.lang$,
  ]).pipe(
    map(([menuParams, lang]) => {
      const localization = this.menuLocalization(lang);
      return menuItemsToSidebarItems(
        this.menuItems,
        menuParams,
        localization,
        this.menuItemCallback,
      );
    }),
  );

  activeMenu$: Observable<string[]> = combineLatest([
    this.sidebarItems$,
    this.store
      .select(pageFeature.selectActiveMenu)
      // Filter out null activeMenu's
      .pipe(switchMap(activeMenu => (activeMenu ? of(activeMenu) : of()))),
  ]).pipe(
    map(
      ([sidebarItems, activeMenuName]) =>
        this.activeMenuPath(activeMenuName, sidebarItems, []) ?? [],
    ),
  );

  private activeMenuPath(
    activeMenuName: MenuItemName,
    sidebarItems: SidebarItem[],
    path: string[],
  ): string[] | undefined {
    if (sidebarItems.length === 0) {
      return undefined;
    }
    for (const sidebarItem of sidebarItems) {
      if (sidebarItem.name === activeMenuName) {
        return [...path, activeMenuName];
      }
      const extendedPath = this.activeMenuPath(
        activeMenuName,
        sidebarItem.children ?? [],
        [...path, sidebarItem.name],
      );
      if (extendedPath) {
        return extendedPath;
      }
    }
    return undefined;
  }

  private pageConfigForMenuItem(
    menuItem: MenuItemName,
  ): PageConfig | undefined {
    return PAGES_CONFIG.find(config => config.menuItem === menuItem);
  }

  private menuItemCallback = (
    menuItem: MenuItemName,
  ): (() => void) | undefined => {
    const pageConfig = this.pageConfigForMenuItem(menuItem);
    if (!pageConfig) {
      return undefined;
    }
    return () =>
      this.store.dispatch(PageActions.goToPage({ page: pageConfig.pageName }));
  };

  private menuLocalization(lang: Lang): Record<MenuItemName, string> {
    return lang.core.sidebarMenu;
  }
}
